import { useState } from "react";
import { X, Check, Sparkles, ShieldCheck, Coins } from "lucide-react";
import { toast } from "sonner";
import { usePro } from "@/context/ProContext";

const BENEFITS = [
  { t: "Filtro Firma «Ovamx Original»",     d: "Sello de calidad reconocible. Tu contenido se ve profesional al instante." },
  { t: "Promoción priorizada en Horizonte", d: "Tus posts aparecen 3× más rápido en Modo Horizonte de tu nicho." },
  { t: "0% comisión en marketplace",        d: "Durante 12 meses. Vendes y cobras lo que generas." },
  { t: "IA personal sin límites",           d: "Redactar, traducir, editar reels — sin caps mensuales." },
  { t: "Verificación automática",           d: "Identidad verificada incluida sin trámites adicionales." },
  { t: "Analítica de creador",              d: "Métricas reales: alcance, retención, audiencia exacta." },
];

export default function UpgradeModal({ open, onClose, reason = null }) {
  const pro = usePro();
  const [plan, setPlan] = useState("pro_monthly");

  if (!open) return null;

  const goPro = async () => {
    try {
      await pro.upgrade(plan);
      toast.success("Bienvenido a Ovamx PRO. Tu Filtro Firma está activo.");
      onClose?.();
    } catch {
      toast.error("No se pudo activar PRO. Intenta de nuevo.");
    }
  };

  return (
    <div data-testid="upgrade-modal" className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-ink/55 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-cream-card border hairline rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative bg-ink text-cream p-8 md:p-10">
          <div className="grain absolute inset-0 opacity-20 pointer-events-none" />
          <button onClick={onClose} data-testid="upgrade-close" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>
          <div className="relative max-w-xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Ovamx PRO
            </p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tighter leading-[0.98]">
              Tu contenido <span className="italic text-coral">tiene firma</span>.
            </h2>
            <p className="mt-3 text-sm text-cream/85 leading-relaxed">
              {reason || "Activa el Filtro «Ovamx Original» y entra a la curaduría priorizada de Horizonte."}
            </p>
          </div>
        </div>

        <div className="p-6 md:p-8 grid grid-cols-12 gap-6">
          <ul className="col-span-12 md:col-span-7 space-y-3">
            {BENEFITS.map((b, i) => (
              <li key={i} data-testid={`benefit-${i}`} className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-coral text-cream flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
                </span>
                <div>
                  <p className="text-sm font-medium text-ink">{b.t}</p>
                  <p className="text-[12.5px] text-ink-mid leading-relaxed">{b.d}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="col-span-12 md:col-span-5 space-y-3">
            <PlanOption
              testid="plan-monthly"
              active={plan === "pro_monthly"}
              onClick={() => setPlan("pro_monthly")}
              tag="mensual"
              price="$9"
              suffix="/mes"
              note="Cancela cuando quieras"
            />
            <PlanOption
              testid="plan-yearly"
              active={plan === "pro_yearly"}
              onClick={() => setPlan("pro_yearly")}
              tag="anual"
              price="$76"
              suffix="/año"
              note="Ahorras 30% (eq. $6.30/mes)"
              highlight
            />

            <button
              onClick={goPro}
              disabled={pro.upgrading}
              data-testid="upgrade-confirm"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-3.5 text-sm hover:bg-coral transition disabled:opacity-60"
            >
              <Coins className="w-4 h-4" />
              {pro.upgrading ? "Activando…" : "Activar Ovamx PRO"}
            </button>

            <p className="text-[11px] text-ink-mid text-center inline-flex items-center justify-center gap-1.5 w-full">
              <ShieldCheck className="w-3 h-3 text-coral" />
              Pago seguro · Cancelas en un toque
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanOption({ testid, active, onClick, tag, price, suffix, note, highlight }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full text-left rounded-xl border-2 p-4 transition ${
        active
          ? "border-coral bg-coral/5"
          : "border-transparent bg-cream-deep/60 hover:bg-cream-deep"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">{tag}</span>
        {highlight && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-coral text-cream">−30%</span>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-serif text-3xl tracking-tighter">{price}</span>
        <span className="text-xs text-ink-mid">{suffix}</span>
      </div>
      <p className="mt-1 text-[11px] text-ink-mid">{note}</p>
    </button>
  );
}
